/**
 * Zugänge zum Kundenportal.
 *
 * Sie stehen jetzt beim Kunden und nicht mehr in der Verwaltung. Vorher
 * gab es beides: eine Kundenakte hier, eine Zugangsliste dort — und wer
 * einen Zugang anlegen wollte, musste den Kunden ein zweites Mal
 * heraussuchen. Ein Zugang gehört zu genau einem Kunden; ihn woanders zu
 * führen macht aus einer Sache zwei.
 *
 * Dazu die Portalansicht: nachsehen, was der Kunde sieht, ohne ihn nach
 * seinem Passwort zu fragen. Diese Sitzung ist schreibgeschützt — eine
 * Behebungsmeldung, die in Wahrheit das Prüfbüro abgesetzt hat, wäre eine
 * Aussage des Kunden, die er nie gemacht hat.
 */

import { useState } from 'react'
import { useKunden } from '../api/abfragen'
import { api } from '../api/klient'
import {
  PORTALROLLENTEXT,
  useBetreiberkontoAendern,
  useBetreiberkontoAnlegen,
  useBetreiberkontoLoeschen,
  useBetreiberkonten,
  type BetreiberkontoZeile,
  type Portalrolle,
  type ZugangAngelegt,
} from '../api/verwaltung'
import { Blatt } from './Blatt'
import { Fehlerhinweis, Ladeanzeige, Leerhinweis, datumDeutsch } from './Hinweise'

export function Portalzugaenge({ fuerKunde }: { fuerKunde?: string }) {
  const kunden = useKunden()
  // Beim Kunden ist die Auswahl überflüssig – er steht schon fest.
  const [kundeId, setzeKundeId] = useState(fuerKunde ?? '')
  const konten = useBetreiberkonten(kundeId || undefined)
  const [neuOffen, setzeNeuOffen] = useState(false)
  const [bearbeitet, setzeBearbeitet] = useState<BetreiberkontoZeile | null>(null)

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1">
          {fuerKunde ? (
            <h2 className="text-xl font-bold">Zugänge zum Kundenportal</h2>
          ) : (
            <>
              <label htmlFor="bk-filter">Kunde</label>
              <select
                id="bk-filter"
                value={kundeId}
                onChange={(e) => setzeKundeId(e.target.value)}
              >
                <option value="">Alle Kunden</option>
                {(kunden.data ?? []).map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.name}
                  </option>
                ))}
              </select>
            </>
          )}
        </div>
        <button type="button" className="knopf-haupt" onClick={() => setzeNeuOffen(true)}>
          Neues Konto
        </button>
      </div>

      <p className="text-base text-slate-600">
        Zugänge zum Kundenportal, in der Regel für den Bauhof. Dort sehen die
        Kunden ihre offenen Mängel und die Berichte. Melden dürfen sie eine
        Behebung – bestätigen darf sie nur der Prüfer bei der Nachprüfung.
      </p>

      {konten.isLoading ? (
        <Ladeanzeige />
      ) : konten.error ? (
        <Fehlerhinweis fehler={konten.error} />
      ) : (konten.data ?? []).length === 0 ? (
        <Leerhinweis
          titel="Noch keine Kundenzugänge"
          text="Legen Sie einen Zugang zum Kundenportal an."
        />
      ) : (
        <ul className="space-y-2">
          {(konten.data ?? []).map((k) => (
            <li key={k.id}>
              <button type="button" className="karte-knopf" onClick={() => setzeBearbeitet(k)}>
                <div className="flex-1">
                  <div className="font-semibold">{k.name}</div>
                  <div className="text-base text-slate-700">{k.email}</div>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    <span className="marke bg-slate-100 text-slate-700">{k.kunde_name}</span>
                    <span className="marke bg-marke-100 text-marke-700">
                      {PORTALROLLENTEXT[k.rolle]}
                    </span>
                    {!k.aktiv && (
                      <span className="marke bg-stufe-3-grund text-stufe-3">gesperrt</span>
                    )}
                    {!k.hat_passwort && (
                      <span className="marke bg-stufe-2-grund text-stufe-2">
                        kein Passwort
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right text-sm text-slate-600">
                  <div>zuletzt</div>
                  <div>{datumDeutsch(k.letzte_anmeldung)}</div>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {neuOffen && (
        <BetreiberNeu
          fuerKunde={fuerKunde}
          schliessen={() => setzeNeuOffen(false)}
        />
      )}
      {bearbeitet && (
        <BetreiberBlatt konto={bearbeitet} schliessen={() => setzeBearbeitet(null)} />
      )}
    </div>
  )
}

function BetreiberNeu({
  fuerKunde,
  schliessen,
}: {
  fuerKunde?: string
  schliessen: () => void
}) {
  const kunden = useKunden()
  const anlegen = useBetreiberkontoAnlegen()
  const [passwort, setzePasswort] = useState('')
  const [selbstSetzen, setzeSelbstSetzen] = useState(false)
  const [senden, setzeSenden] = useState(true)
  const [einmalig, setzeEinmalig] = useState(true)
  const [angelegt, setzeAngelegt] = useState<ZugangAngelegt | null>(null)

  return (
    <Blatt
      titel="Neuer Kundenzugang"
      schliessen={schliessen}
      fuss={
        <div className="flex gap-3">
          <button type="button" className="knopf-still flex-1" onClick={schliessen}>
            Abbrechen
          </button>
          <button
            type="submit"
            form="bk-neu"
            className="knopf-haupt flex-1"
            disabled={anlegen.isPending}
          >
            {anlegen.isPending ? 'Legt an …' : 'Anlegen'}
          </button>
        </div>
      }
    >
      <form
        id="bk-neu"
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault()
          const formular = new FormData(e.currentTarget)
          anlegen.mutate(
            {
              kunde_id: String(formular.get('kunde_id')),
              email: String(formular.get('email')),
              name: String(formular.get('name')),
              rolle: String(formular.get('rolle')) as Portalrolle,
              passwort: selbstSetzen && passwort.trim() ? passwort : undefined,
              passwort_erzeugen: !selbstSetzen,
              zugangsdaten_senden: senden,
              einmalpasswort: einmalig,
            },
            // Nicht sofort schließen: bei gesperrtem Versand steht das
            // Passwort in der Antwort, und es gibt kein zweites Mal.
            { onSuccess: setzeAngelegt },
          )
        }}
      >
        {/* Steht der Kunde schon fest, wird er nicht noch einmal gefragt.
            Genau das war der Umweg: den Kunden ein zweites Mal
            heraussuchen, obwohl man von seiner Akte gekommen ist. */}
        {fuerKunde ? (
          <input type="hidden" name="kunde_id" value={fuerKunde} />
        ) : (
          <div>
            <label htmlFor="bk-kunde">Kunde</label>
            <select id="bk-kunde" name="kunde_id" required defaultValue="">
              <option value="" disabled>
                Bitte wählen
              </option>
              {(kunden.data ?? []).map((k) => (
                <option key={k.id} value={k.id}>
                  {k.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label htmlFor="bk-name">Name</label>
          <input id="bk-name" name="name" required maxLength={120} placeholder="Bauhof" />
        </div>
        <div>
          <label htmlFor="bk-email">E-Mail</label>
          <input id="bk-email" name="email" type="email" required autoCapitalize="none" />
        </div>
        <div>
          <label htmlFor="bk-rolle">Rolle im Kundenportal</label>
          <select id="bk-rolle" name="rolle" defaultValue="mitarbeiter">
            <option value="mitarbeiter">Mitarbeiter</option>
            <option value="leitung">Leitung</option>
          </select>
        </div>
        <fieldset className="space-y-2">
          <legend className="mb-1 block font-semibold text-slate-800">Passwort</legend>

          <label className="flex items-start gap-3 rounded-feld border-2 border-slate-200 p-2 font-normal">
            <input
              type="checkbox"
              checked={senden}
              onChange={(e) => setzeSenden(e.target.checked)}
              className="mt-1 h-6 w-6 shrink-0"
              style={{ minHeight: 'auto' }}
            />
            <span>
              <span className="font-semibold">Zugangsdaten per E-Mail schicken</span>
              <span className="block text-base text-slate-600">
                An die Adresse oben, mit der Portaladresse und dem Passwort.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-3 rounded-feld border-2 border-slate-200 p-2 font-normal">
            <input
              type="checkbox"
              checked={einmalig}
              onChange={(e) => setzeEinmalig(e.target.checked)}
              className="mt-1 h-6 w-6 shrink-0"
              style={{ minHeight: 'auto' }}
            />
            <span>
              <span className="font-semibold">Einmalpasswort</span>
              <span className="block text-base text-slate-600">
                Gilt nur bis zur ersten eigenen Wahl. Empfohlen, solange das
                Passwort per Mail geht — es steht dann in mindestens zwei
                Postfächern.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-3 rounded-feld border-2 border-slate-200 p-2 font-normal">
            <input
              type="checkbox"
              checked={selbstSetzen}
              onChange={(e) => setzeSelbstSetzen(e.target.checked)}
              className="mt-1 h-6 w-6 shrink-0"
              style={{ minHeight: 'auto' }}
            />
            <span>
              <span className="font-semibold">Passwort selbst vergeben</span>
              <span className="block text-base text-slate-600">
                Ohne Haken wird eines erzeugt — länger und zufälliger als eines,
                das man sich ausdenkt.
              </span>
            </span>
          </label>

          {selbstSetzen && (
            <input
              id="bk-passwort"
              type="text"
              value={passwort}
              onChange={(e) => setzePasswort(e.target.value)}
              minLength={12}
              autoComplete="off"
              placeholder="mindestens 12 Zeichen"
            />
          )}
        </fieldset>

        <Fehlerhinweis fehler={anlegen.error} />

        {angelegt && (
          <div
            className={`rounded-feld border-2 p-3 ${
              angelegt.mail_verschickt
                ? 'border-stufe-ob bg-stufe-ob-grund'
                : 'border-stufe-2 bg-stufe-2-grund'
            }`}
          >
            <p
              className={`font-bold ${
                angelegt.mail_verschickt ? 'text-stufe-ob' : 'text-stufe-2'
              }`}
            >
              Zugang angelegt
              {angelegt.mail_verschickt ? ' und Zugangsdaten verschickt.' : '.'}
            </p>

            {angelegt.passwort && (
              <>
                <p className="mt-2 text-base">{angelegt.hinweis}</p>
                <p className="mt-2 select-all rounded-feld bg-white p-2 font-mono text-lg">
                  {angelegt.passwort}
                </p>
                <p className="mt-1 text-base">
                  Notieren Sie es jetzt — gespeichert ist nur der Hash, ein zweites
                  Mal lässt es sich nicht anzeigen.
                </p>
              </>
            )}

            <button
              type="button"
              className="knopf-haupt mt-3"
              onClick={schliessen}
            >
              Fertig
            </button>
          </div>
        )}
      </form>
    </Blatt>
  )
}

function BetreiberBlatt({
  konto,
  schliessen,
}: {
  konto: BetreiberkontoZeile
  schliessen: () => void
}) {
  const aendern = useBetreiberkontoAendern()
  const loeschen = useBetreiberkontoLoeschen()
  const [ansehen, setzeAnsehen] = useState(false)
  const [name, setzeName] = useState(konto.name)
  const [rolle, setzeRolle] = useState<Portalrolle>(konto.rolle)
  const [aktiv, setzeAktiv] = useState(konto.aktiv)
  const [passwort, setzePasswort] = useState('')
  const [loeschenBestaetigt, setzeLoeschenBestaetigt] = useState(false)

  return (
    <Blatt
      titel={konto.name}
      untertitel={`${konto.email} · ${konto.kunde_name}`}
      schliessen={schliessen}
      fuss={
        <div className="flex gap-3">
          <button type="button" className="knopf-still flex-1" onClick={schliessen}>
            Abbrechen
          </button>
          <button
            type="button"
            className="knopf-haupt flex-1"
            disabled={aendern.isPending}
            onClick={() =>
              aendern.mutate(
                {
                  id: konto.id,
                  name,
                  rolle,
                  aktiv,
                  passwort: passwort.trim() ? passwort : undefined,
                },
                { onSuccess: schliessen },
              )
            }
          >
            {aendern.isPending ? 'Speichert …' : 'Speichern'}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Nachsehen, was der Kunde sieht – ohne ihn nach seinem Passwort
            zu fragen. Das wäre der falsche Weg und gewöhnte ihn außerdem
            daran, es herauszugeben.

            Die Sitzung ist schreibgeschützt, erzwungen im Server: eine
            Behebungsmeldung, die in Wahrheit von hier kommt, wäre eine
            Aussage des Kunden, die er nie gemacht hat. */}
        {konto.aktiv && (
          <div className="rounded-feld border-2 border-slate-200 p-3">
            <button
              type="button"
              className="knopf-zweit w-full"
              disabled={ansehen}
              onClick={() => {
                setzeAnsehen(true)
                void api
                  .senden(`/api/betreiberkonten/${konto.id}/ansehen`, {})
                  .then(() => {
                    // Ganzes Neuladen und kein Wechsel im Rahmen: das
                    // Portal ist eine eigene Anwendung mit eigenem Cookie.
                    window.location.href = '/portal'
                  })
                  .finally(() => setzeAnsehen(false))
              }}
            >
              {ansehen ? 'Öffnet …' : 'Portal als dieser Zugang ansehen'}
            </button>
            <p className="mt-1 text-base text-slate-600">
              Sie sehen das Kundenportal genau so wie {konto.name}. Ändern
              können Sie dort nichts — und Ihre Anmeldung im Prüfbüro bleibt
              bestehen; über „Abmelden" im Portal kommen Sie zurück.
            </p>
          </div>
        )}

        <div>
          <label htmlFor="bke-name">Name</label>
          <input
            id="bke-name"
            value={name}
            onChange={(e) => setzeName(e.target.value)}
            maxLength={120}
          />
        </div>
        <div>
          <label htmlFor="bke-rolle">Rolle im Kundenportal</label>
          <select
            id="bke-rolle"
            value={rolle}
            onChange={(e) => setzeRolle(e.target.value as Portalrolle)}
          >
            <option value="mitarbeiter">Mitarbeiter</option>
            <option value="leitung">Leitung</option>
          </select>
        </div>

        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={aktiv}
            onChange={(e) => setzeAktiv(e.target.checked)}
            className="mt-1 h-6 w-6"
          />
          <span className="font-normal">
            <span className="font-semibold">Konto aktiv</span>
            <span className="block text-base text-slate-600">
              Beim Sperren werden die offenen Sitzungen im Kundenportal sofort beendet.
            </span>
          </span>
        </label>

        <div>
          <label htmlFor="bke-passwort">Neues Passwort</label>
          <input
            id="bke-passwort"
            type="text"
            value={passwort}
            onChange={(e) => setzePasswort(e.target.value)}
            minLength={12}
            autoComplete="off"
            placeholder="leer lassen = unverändert"
          />
          <p className="mt-1 text-base text-slate-600">
            Ein neues Passwort beendet ebenfalls alle offenen Sitzungen im Kundenportal.
          </p>
        </div>

        <Fehlerhinweis fehler={aendern.error} />

        <div className="border-t-2 border-slate-200 pt-4">
          {loeschenBestaetigt ? (
            <div className="space-y-2">
              <p className="font-semibold">
                Zugang endgültig entfernen? Die bisherigen Meldungen dieses Kontos
                bleiben erhalten – sie gehören zum Lebenslauf der Mängel.
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  className="knopf-still flex-1"
                  onClick={() => setzeLoeschenBestaetigt(false)}
                >
                  Doch nicht
                </button>
                <button
                  type="button"
                  className="knopf-warnung flex-1"
                  disabled={loeschen.isPending}
                  onClick={() => loeschen.mutate(konto.id, { onSuccess: schliessen })}
                >
                  {loeschen.isPending ? 'Entfernt …' : 'Ja, entfernen'}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="knopf-warnung"
              onClick={() => setzeLoeschenBestaetigt(true)}
            >
              Zugang entfernen
            </button>
          )}
          <Fehlerhinweis fehler={loeschen.error} />
        </div>
      </div>
    </Blatt>
  )
}


/**
 * Die einzelnen Rechte eines Benutzers.
 *
 * Die Rolle ist die Vorauswahl, nicht das letzte Wort. Hier steht, was
 * daraus folgt — und vor allem, woher jedes Recht kommt: aus der Rolle
 * oder ausdrücklich erteilt beziehungsweise entzogen. Ohne diese
 * Unterscheidung sähe man nur Häkchen und wüsste nicht, was beim
 * Rollenwechsel mitwandert und was bleibt.
 */
